import { Store } from '@/types/store';
import { format, isValid, parse } from 'date-fns';
import type { Locale as DateFnsLocale } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';

type SupportedLanguage = 'en' | 'fr';

const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
const dotDateRegex = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
const isoDateFormat = 'yyyy-MM-dd';

const dateFormats: Record<SupportedLanguage, string> = {
  en: 'MM/dd/yyyy',
  fr: 'dd/MM/yyyy',
};

const dateLocales: Record<SupportedLanguage, DateFnsLocale> = {
  en: enUS,
  fr,
};

const normalizeLanguage = (language?: string): SupportedLanguage => {
  const normalized = language?.toLowerCase().split('-')[0];
  return normalized === 'en' ? 'en' : 'fr';
};

const parseDotDateString = (dateString: string): Date | null => {
  const match = dateString.match(dotDateRegex);
  if (!match) return null;

  const [, dayStr, monthStr, yearStr] = match;
  const day = Number(dayStr);
  const month = Number(monthStr);
  const year = Number(yearStr);
  const parsed = new Date(year, month - 1, day);

  if (
    parsed.getFullYear() === year &&
    parsed.getMonth() === month - 1 &&
    parsed.getDate() === day
  ) {
    return parsed;
  }

  return null;
};

const parseIsoDateString = (dateString: string): Date | null => {
  if (!isoDateRegex.test(dateString)) return null;
  const parsed = new Date(dateString);
  return isValid(parsed) ? parsed : null;
};

const fallbackDateFormats: Record<SupportedLanguage, string[]> = {
  en: ['MM/dd/yyyy', 'MM-dd-yyyy', 'yyyy/MM/dd', 'yyyy-MM-dd', 'yyyyMMdd'],
  fr: ['dd/MM/yyyy', 'dd-MM-yyyy', 'yyyy/MM/dd', 'yyyy-MM-dd', 'dd.MM.yyyy'],
};

const tryParseWithFormats = (
  value: string,
  formats: string[],
  locale: DateFnsLocale
): Date | null => {
  for (const formatString of formats) {
    const parsed = parse(value, formatString, new Date(), {
      locale,
      useAdditionalDayOfYearTokens: false,
      useAdditionalWeekYearTokens: false,
    });
    if (isValid(parsed)) {
      return parsed;
    }
  }
  return null;
};

const parseFlexibleDateString = (
  dateString: string,
  language?: string
): Date | null => {
  if (!dateString) return null;
  const trimmed = dateString.trim();
  if (!trimmed) return null;

  return (
    parseDotDateString(trimmed) ??
    parseIsoDateString(trimmed) ??
    ((): Date | null => {
      const languagesToTry: SupportedLanguage[] = language
        ? [normalizeLanguage(language)]
        : (['en', 'fr'] as SupportedLanguage[]);
      for (const lang of languagesToTry) {
        const parsed = tryParseWithFormats(
          trimmed,
          fallbackDateFormats[lang],
          dateLocales[lang]
        );
        if (parsed) {
          return parsed;
        }
      }
      return null;
    })()
  );
};

export const parseDateValue = (
  value: Date | string | null | undefined,
  language?: string
): Date | null => {
  if (!value) return null;
  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }
  return parseFlexibleDateString(value, language);
};

export const getLocalizedDateFormat = (language?: string): string =>
  dateFormats[normalizeLanguage(language)];

export const getLocalizedPlaceholder = (language?: string): string =>
  getLocalizedDateFormat(language).toUpperCase();

export const getDayPickerLocale = (language?: string): DateFnsLocale =>
  dateLocales[normalizeLanguage(language)];

/**
 * Gets the date-fns locale from i18n language code
 * @param i18nLanguage - Language code from i18n (e.g., 'fr', 'fr-FR', 'en', 'en-US')
 * @returns DateFnsLocale (fr or enUS)
 */
export const getDateFnsLocaleFromI18n = (
  i18nLanguage?: string | null
): DateFnsLocale => {
  return dateLocales[normalizeLanguage(i18nLanguage ?? undefined)];
};

export const getDayPickerWeekStartsOn = (store: Store): 0 | 1 =>
  store === Store.FR ? 1 : 0;

export const formatLocalizedDate = (
  value: Date | string | null | undefined,
  language?: string
): string => {
  const date = parseDateValue(value, language);
  if (!date) return '';
  return format(date, getLocalizedDateFormat(language), {
    locale: getDayPickerLocale(language),
  });
};

export const formatIsoDate = (
  value: Date | string | null | undefined
): string | null => {
  const date = parseDateValue(value);
  if (!date) return null;
  return format(date, isoDateFormat);
};

export const parseLocalizedInputValue = (
  value: string,
  language?: string
): Date | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const parsed = parse(trimmed, getLocalizedDateFormat(language), new Date(), {
    locale: getDayPickerLocale(language),
  });
  return isValid(parsed) ? parsed : null;
};
